import { HierarchyCard, NavigationSlider } from '@yes.technology/react-toolkit'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import {
  ActivityValue,
  FlatActivityGroup,
  FlatActivityModel,
  NestableGroupValue
} from 'types/shared'
import Activity from './Activity'
import styled from 'styled-components'

type ActivityGroupProps = {
  groups: NestableGroupValue
  activityModel: FlatActivityGroup
  activityModels: Record<string, FlatActivityModel>
  parentPath: string[]
}

const reorderActivityValues = (
  activityValues: Record<string, ActivityValue | NestableGroupValue>,
  visibleActivityValuesIds: string[],
  activityModelOrder: string[]
) => {
  const reorderedValues: Record<string, ActivityValue | NestableGroupValue> = {}

  activityModelOrder.forEach((activityId) => {
    if (visibleActivityValuesIds.includes(activityId)) {
      reorderedValues[activityId] = activityValues[activityId]
    }
  })

  Object.keys(activityValues).forEach((activityId) => {
    if (
      !reorderedValues.hasOwnProperty(activityId) &&
      visibleActivityValuesIds.includes(activityId)
    ) {
      reorderedValues[activityId] = activityValues[activityId]
    }
  })

  return reorderedValues
}

const Card = styled(HierarchyCard)`
  > div {
    padding: 0px;
  }
`

const TitleWrapper = styled.div`
  padding: 8px;
`

const ContentWrapper = styled.div`
  padding: 8px;
  padding-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ActivityGroup = ({
  groups,
  activityModel,
  activityModels,
  parentPath
}: ActivityGroupProps) => {
  const [currentGroup, setCurrentGroup] = useState(1)
  const { t } = useTranslation()

  const orderedValues = useMemo(() => {
    const groupValues = groups[currentGroup - 1]?.values || {}
    const visibleActivityValuesIds = Object.keys(groupValues).filter(
      (activityId) => !activityModels[activityId]?.hide_in_review
    )

    return Object.keys(
      reorderActivityValues(
        groupValues,
        visibleActivityValuesIds,
        activityModel.activity_models
      )
    )
  }, [currentGroup, groups, activityModel, activityModels])

  if (!orderedValues.length) {
    return null
  }

  return (
    <Card>
      <TitleWrapper>
        <h2>{activityModel.display_text.des}</h2>
      </TitleWrapper>
      <NavigationSlider
        pulseAnimateOnNext
        currentPosition={currentGroup}
        first={1}
        total={groups.length}
        hierarchyLevel='level_2'
        textForCurrent={t('activity-group.navigation-slider.text-for-current')}
        afterClick={setCurrentGroup}
        heightClass='small'
      />
      <ContentWrapper>
        <Activity
          activityModelIds={orderedValues}
          parentPath={[...parentPath, (currentGroup - 1).toString(), 'values']}
        />
      </ContentWrapper>
    </Card>
  )
}

export default ActivityGroup
